import { useState } from "react";
import { ChevronDown, Send, Loader2, CornerDownRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmt = (d) => new Date(d).toLocaleString("el-GR", { dateStyle: "short", timeStyle: "short" });

const MessageRow = ({ m, open, onToggle, onReplied }) => {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/contacts/${m.id}/reply`, { message: text.trim() });
      onReplied(m.id, data);
      setText("");
      toast.success(`Η απάντηση στάλθηκε στο ${m.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Η αποστολή απέτυχε");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`rounded-2xl border p-4 transition-colors ${m.read ? "bg-white border-border" : "bg-red-50/60 border-red-200"}`} data-testid="admin-message-row">
      <button onClick={() => onToggle(m)} className="w-full flex items-center justify-between gap-3 text-left" data-testid="admin-message-toggle">
        <span className="font-semibold flex items-center gap-2 min-w-0">
          {!m.read && <span data-testid="admin-message-unread-dot" className="h-2.5 w-2.5 rounded-full bg-red-500 shrink-0" />}
          <span className="truncate">{m.name} <span className="text-muted-foreground font-normal">· {m.email}</span></span>
        </span>
        <span className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
          {(m.replies || []).length > 0 && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">Απαντήθηκε</span>}
          {fmt(m.created_at)}
          <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      <p className="text-xs font-bold text-primary mt-1">{m.subject}</p>
      <p className={`text-sm text-foreground/80 mt-1 whitespace-pre-line ${open ? "" : "line-clamp-1"}`}>{m.message}</p>
      {open && (
        <div className="mt-4 space-y-3" data-testid="admin-message-detail">
          {(m.replies || []).map((r, i) => (
            <div key={i} className="flex gap-2 text-sm bg-sky-50 rounded-xl p-3" data-testid="admin-message-reply-item">
              <CornerDownRight size={16} className="text-primary shrink-0 mt-0.5" />
              <div><p className="whitespace-pre-line">{r.message}</p><p className="text-[11px] text-muted-foreground mt-1">{fmt(r.created_at)}</p></div>
            </div>
          ))}
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder={`Απάντηση προς ${m.name}...`}
            data-testid="admin-message-reply-input" className="w-full rounded-xl bg-muted p-3 text-sm outline-none focus:ring-2 ring-primary" />
          <div className="flex justify-end">
            <button onClick={send} disabled={sending || !text.trim()} data-testid="admin-message-reply-send"
              className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-primary text-white font-bold text-sm disabled:opacity-50 hover:brightness-110 transition-[filter]">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Αποστολή απάντησης
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const MessagesTab = ({ messages, setMessages }) => {
  const [openId, setOpenId] = useState(null);

  const toggle = (m) => {
    setOpenId((id) => (id === m.id ? null : m.id));
    if (!m.read) {
      setMessages((ms) => ms.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
      api.post(`/admin/contacts/${m.id}/read`).catch(() => {});
    }
  };

  const onReplied = (id, reply) =>
    setMessages((ms) => ms.map((x) => (x.id === id ? { ...x, read: true, replies: [...(x.replies || []), reply] } : x)));

  return (
    <section>
      <h2 className="font-display text-2xl font-bold mb-5">Μηνύματα επικοινωνίας</h2>
      <div className="space-y-3">
        {messages.length === 0 && <p className="text-muted-foreground">Δεν υπάρχουν μηνύματα.</p>}
        {messages.map((m) => <MessageRow key={m.id} m={m} open={openId === m.id} onToggle={toggle} onReplied={onReplied} />)}
      </div>
    </section>
  );
};
